import React from 'react'

import { MoveIcon } from 'lucide-react'
import { Marker } from 'react-map-gl/mapbox'

import { Button } from '~/client/src/shared/components/Button/Button'
import { WorkflowGlyph } from '~/client/src/shared/components/WorkflowTypeIcon/WorkflowIconGroups'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import DomPin from './DomPin'

interface IProps {
  latitude: number
  longitude: number
  iconName?: string
  // The workflow's own colour. Omitted only by callers with no workflow behind the pin.
  fill?: string
  isAssigned?: boolean
  canMove?: boolean
  // Once the user interacts with markups (or presses Move) the full pill collapses
  // to the corner badge for the rest of the session.
  collapsed?: boolean
  // Workflows map: skip the "Move" pill entirely — only the minimalistic badge.
  badgeOnly?: boolean
  onMove?: () => void
}

const DomMovablePin = ({
  latitude,
  longitude,
  iconName,
  fill,
  isAssigned,
  canMove,
  collapsed,
  badgeOnly,
  onMove,
}: IProps): JSX.Element => {
  const enabled = !!canMove && !!onMove
  // Workflows map (badgeOnly) shows only the badge; Precise Location keeps the
  // "Move" pill until the first interaction, then collapses to the badge.
  const showPill = enabled && !collapsed && !badgeOnly
  const showBadge = enabled && (!!collapsed || !!badgeOnly)
  // Inertness during an active gesture is owned by the markup editor's container
  // classes (.markup-drawing / .markup-dragging force every marker inert, so a
  // stroke/drag is never eaten); the pin just advertises itself as clickable, so
  // it stays a pointer target while a shape is merely selected at rest.
  const interactive = enabled
  return (
    <Marker latitude={latitude} longitude={longitude} style={{ zIndex: 2 }}>
      <div className="tw:group/pin-move tw:relative tw:flex tw:flex-col tw:items-center">
        <div
          onPointerDown={interactive ? e => e.stopPropagation() : undefined}
          // The editor's .markup-drawing/.markup-dragging classes force this marker
          // inert during a stroke or drag (so it never eats the gesture); at rest —
          // including while a shape is selected — it's a normal pointer target.
          className={`${
            interactive
              ? 'tw:pointer-events-auto tw:cursor-pointer'
              : 'tw:pointer-events-none'
          }`}
        >
          <DomPin
            overlay
            fill={fill}
            // The star and the Move affordance both sit on the head's top-right rim, and a
            // movable pin's surface is about moving: the badge owns that corner. The star
            // rides the thumbnail, where there is nothing to move.
            isAssigned={isAssigned && !enabled}
            glyph={({ size, color }) => (
              <WorkflowGlyph iconName={iconName} size={size} color={color} />
            )}
            onClick={interactive ? onMove : undefined}
          />
        </div>
        {showPill && (
          <Button
            variant="primary-outline"
            size="sm"
            onClick={interactive ? onMove : undefined}
            onPointerDown={interactive ? e => e.stopPropagation() : undefined}
            // Opaque hover (over the map primary-outline's translucent soft hover
            // would let the map bleed through).
            className={`${
              interactive ? 'tw:pointer-events-auto' : 'tw:pointer-events-none'
            } tw:absolute tw:top-full tw:mt-1 tw:shadow-lg tw:hover:bg-muted tw:group-hover/pin-move:bg-muted`}
          >
            <MoveIcon data-icon="inline-start" className="tw:size-3.5" />
            {Localization.translator.move}
          </Button>
        )}
        {showBadge && (
          <Button
            variant="primary-outline"
            size="icon-xs"
            aria-label={Localization.translator.move}
            onClick={onMove}
            onPointerDown={e => e.stopPropagation()}
            // White circle + 1.5px brand-blue stroke + Move glyph, layered onto the
            // pin head's top-right rim. The head is a 48px svg (~16px-radius disk)
            // centered on the wrapper's x-axis; these offsets land the badge center on
            // the circle's rim at 45 degrees, so it overlaps rather than floats.
            className={`tw:absolute tw:z-10 tw:-top-[52px] tw:-left-px tw:size-5 tw:rounded-full tw:border-[1.5px] tw:border-primary tw:bg-background tw:p-1 tw:hover:bg-muted tw:group-hover/pin-move:bg-muted ${
              interactive ? 'tw:pointer-events-auto' : 'tw:pointer-events-none'
            }`}
          >
            <MoveIcon className="tw:size-3" />
          </Button>
        )}
      </div>
    </Marker>
  )
}

export default DomMovablePin
